/**
 * AskBar — one input for questions and drill-down commands.
 * Parsing lives in commands.js; this component only collects text.
 *
 * Props:
 *   placeholder  string
 *   onSubmit     (text) => void
 *   busy         boolean — an /ask or /resolve is in flight
 *   status       { text, tone: 'info'|'warn' } | null — one-line feedback
 */

import { memo, useState } from 'react';
import { CornerDownLeft, MessageSquareText } from 'lucide-react';
import './KnowledgeCards.css';

function AskBarImpl({ placeholder, onSubmit, busy = false, status = null }) {
  const [text, setText] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || busy) return;
    onSubmit(value);
    setText('');
  };

  return (
    <form className="kx-ask" onSubmit={submit} role="search" aria-label="Ask about a part">
      {status?.text && (
        <p className={`kx-ask__status${status.tone === 'warn' ? ' is-warn' : ''}`} role="status" aria-live="polite">
          {status.text}
        </p>
      )}
      <div className="kx-ask__field">
        <MessageSquareText size={15} className="kx-ask__icon" aria-hidden="true" />
        <input
          className="kx-ask__input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          autoComplete="off"
          spellCheck="false"
          onKeyDown={(e) => {
            // Esc with text clears the field; with an empty field the view handles it.
            if (e.key === 'Escape' && text) setText('');
          }}
        />
        <span className="kx-ask__hint" aria-hidden="true">open · deeper · back · reset</span>
        <button type="submit" className="kx-ask__send" disabled={!text.trim() || busy} aria-label="Send">
          {busy ? <span className="kx-ask__spinner" aria-hidden="true" /> : <CornerDownLeft size={14} aria-hidden="true" />}
        </button>
      </div>
    </form>
  );
}

export const AskBar = memo(AskBarImpl);
export default AskBar;
